import React, { useEffect, useState } from 'react';
import {
    TextField,
    Grid,
    FormControlLabel,
    Switch,
    InputAdornment,
    CircularProgress,
    Snackbar,
    Alert,
} from '@mui/material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { getDeliveryChargeById, createDeliveryCharge, updateDeliveryCharge } from '../../../shared/services/deliveryChargeService';
import { DeliveryCharge } from '../../../shared/types/deliveryCharge';

interface DeliveryChargeFormState extends Omit<DeliveryCharge, 'id' | 'createdAt' | 'updatedAt' | 'minOrderAmount' | 'charge'> {
    minOrderAmount: number | string;
    charge: number | string;
}

export const AddEditDeliveryCharge: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState<DeliveryChargeFormState>({
        minOrderAmount: 0,
        charge: 0,
        isActive: true
    });

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: 'success' as 'success' | 'error', message: '' });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        if (isEdit && id) {
            const fetchCharge = async () => {
                try {
                    setFetching(true);
                    const response = await getDeliveryChargeById(id);
                    if (response.success && response.data) {
                        setFormData({
                            minOrderAmount: response.data.minOrderAmount,
                            charge: response.data.charge,
                            isActive: response.data.isActive
                        });
                    }
                } catch (error: any) {
                    console.error('Error fetching delivery charge:', error);
                    setAlert({ show: true, type: 'error', message: 'Error loading delivery charge details' });
                } finally {
                    setFetching(false);
                }
            };
            fetchCharge();
        }
    }, [id, isEdit]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        let finalValue: any = value;
        if (type === 'checkbox') {
            finalValue = checked;
        } else if (type === 'number') {
            if (value !== '' && Number(value) < 0) {
                return;
            }
            finalValue = value === '' ? '' : Number(value);
        }

        setFormData(prev => ({
            ...prev,
            [name]: finalValue
        }));

        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (value === '0') {
            setFormData(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validate = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (formData.minOrderAmount === undefined || formData.minOrderAmount === null || formData.minOrderAmount === '') {
            newErrors.minOrderAmount = 'Minimum order amount is required.';
        }
        if (formData.charge === undefined || formData.charge === null || formData.charge === '') {
            newErrors.charge = 'Delivery charge is required.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);

            const payload = {
                minOrderAmount: Number(formData.minOrderAmount),
                charge: Number(formData.charge),
                isActive: formData.isActive
            };

            if (isEdit && id) {
                await updateDeliveryCharge(id, payload);
                setAlert({ show: true, type: 'success', message: 'Delivery charge updated successfully' });
            } else {
                await createDeliveryCharge(payload);
                setAlert({ show: true, type: 'success', message: 'Delivery charge created successfully' });
            }
            setTimeout(() => {
                navigate('/delivery-charge');
            }, 1000);
        } catch (error: any) {
            console.error('Error saving delivery charge:', error);
            setAlert({
                show: true,
                type: 'error',
                message: error.response?.data?.message || 'Failed to save delivery charge'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-edit-admin-container p-[40px]">
            <div className="add-edit-admin-content">
                <div className="form-wrapper max-w-6xl">
                    <div className="-ml-4">
                        <Link to="/delivery-charge" className="back-link">
                            <ArrowBack className="text-[#0EA5E9]" />
                            <span className="ml-2 text-[#0EA5E9]">Back</span>
                        </Link>
                    </div>
                    <div className="title text-2xl sm:text-5xl">
                        {isEdit ? 'Edit Delivery Charge' : 'Add Delivery Charge'}
                    </div>

                    <div className="form-card">
                        {fetching ? (
                            <div className="flex flex-col items-center justify-center p-12">
                                <CircularProgress size={50} />
                                <p className="mt-4 text-gray-500">Loading details...</p>
                            </div>
                        ) : (
                            <>
                                {alert.show && (
                                    <Alert
                                        severity={alert.type}
                                        sx={{ mb: 4, borderRadius: '12px' }}
                                        onClose={() => setAlert({ ...alert, show: false })}
                                    >
                                        {alert.message}
                                    </Alert>
                                )}

                                <form onSubmit={handleSubmit} className="form space-y-4">
                                    <div className="mb-6">
                                        <div className="text-2xl font-bold tracking-tight text-gray-900">
                                            Charge Information
                                        </div>
                                    </div>

                                    <Grid container spacing={3}>
                                        {/* Min Order Amount */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Minimum Order Amount"
                                                name="minOrderAmount"
                                                type="number"
                                                value={formData.minOrderAmount}
                                                onChange={handleChange}
                                                onFocus={handleFocus}
                                                fullWidth
                                                required
                                                error={!!errors.minOrderAmount}
                                                helperText={errors.minOrderAmount}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                }}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>

                                        {/* Delivery Charge */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Delivery Charge"
                                                name="charge"
                                                type="number"
                                                value={formData.charge}
                                                onChange={handleChange}
                                                onFocus={handleFocus}
                                                fullWidth
                                                required
                                                error={!!errors.charge}
                                                helperText={errors.charge}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                }}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>

                                        {/* Active Switch */}
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        name="isActive"
                                                        checked={formData.isActive}
                                                        onChange={handleChange}
                                                        color="primary"
                                                    />
                                                }
                                                label="Is Active"
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Action Buttons */}
                                    <div className="form-actions mt-8">
                                        <AppButton
                                            type="button"
                                            variant="cancel"
                                            onClick={() => navigate('/delivery-charge')}
                                            sx={{
                                                minWidth: '120px',
                                                backgroundColor: '#8E8E93',
                                                borderColor: '#8E8E93',
                                                color: '#fff',
                                                '&:hover': { backgroundColor: '#7a7a7f', borderColor: '#7a7a7f' }
                                            }}
                                        >
                                            Cancel
                                        </AppButton>

                                        <AppButton
                                            type="submit"
                                            variant="primary"
                                            disabled={loading}
                                            loading={loading}
                                            sx={{ minWidth: '120px' }}
                                        >
                                            {isEdit ? 'Update Charge' : 'Create Charge'}
                                        </AppButton>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Snackbar */}
            <Snackbar
                open={alert.show}
                autoHideDuration={4000}
                onClose={() => setAlert({ ...alert, show: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={alert.type} variant="filled">
                    {alert.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
